import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  DollarSign,
  ArrowRight,
  RefreshCw,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLanguage, translations } from "../context/LanguageContext";
import { DonationSuccessView } from "../components/donate/DonationSuccessView";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const presetAmounts = [
  { id: "25", amount: 25 },
  { id: "50", amount: 50 },
  { id: "100", amount: 100 },
  { id: "250", amount: 250 },
];

const DonatePage = () => {
  const { language } = useLanguage();
  const t = translations[language].donate;
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [emergencyFund, setEmergencyFund] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Success polling state
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const pollPaymentStatus = useCallback(async (sid, attempts) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }
    setIsPolling(true);
    try {
      const res = await axios.get(`${API}/donations/status/${sid}`);
      if (res.data.payment_status === "paid") {
        setPaymentStatus("success");
        setIsPolling(false);
        return;
      } else if (res.data.status === "expired") {
        setPaymentStatus("expired");
        setIsPolling(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch {
      setPaymentStatus("error");
      setIsPolling(false);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId, pollPaymentStatus]);

  const handleDonate = async () => {
    const amount = selectedPreset
      ? null
      : parseFloat(customAmount);

    if (!selectedPreset && (!amount || amount < 1)) return;

    setIsProcessing(true);
    try {
      const res = await axios.post(`${API}/donations/create-session`, {
        preset_id: selectedPreset || undefined,
        custom_amount: selectedPreset ? undefined : amount,
        recurring,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        origin_url: window.location.origin,
        designation: emergencyFund ? "emergency_survivor_fund" : undefined,
      });
      window.location.href = res.data.url;
    } catch {
      setIsProcessing(false);
    }
  };

  // Success view
  if (sessionId) {
    return <DonationSuccessView isPolling={isPolling} paymentStatus={paymentStatus} t={t} />;
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gray-50" role="banner">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.heroOverline}
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              data-testid="donate-hero-title"
            >
              {t.heroTitle}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600">
              {t.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16" role="region" aria-label={t.impactLabel}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {t.impactItems.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-warm-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-7 h-7 text-warm-700" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Illustrative giving examples */}
      <section className="pb-16" role="region" aria-label={t.givingExamplesTitle}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-warm-50 border border-warm-100 rounded-2xl p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {t.givingExamplesTitle}
            </h2>
            <p className="text-xs text-gray-500 italic mb-6">{t.givingExamplesNote}</p>
            <ul className="space-y-3">
              {t.givingExamples?.map((ex) => (
                <li key={ex.amount} className="flex items-start gap-4 text-sm text-gray-700" data-testid={`giving-example-${ex.amount}`}>
                  <span className="font-bold text-warm-800 text-lg w-16 flex-shrink-0" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>${ex.amount}</span>
                  <span className="pt-1">{ex.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20 bg-gray-50" role="region" aria-label={t.formLabel}>
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.formTitle}
            </h2>
            <p className="text-gray-600">{t.formSubtitle}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
          >
            {/* Frequency Toggle */}
            <div className="flex bg-gray-100 rounded-full p-1 mb-8" role="radiogroup" aria-label={t.frequency}>
              <button
                onClick={() => setRecurring(false)}
                className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
                  !recurring ? 'bg-warm-700 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
                role="radio"
                aria-checked={!recurring}
                data-testid="donate-one-time-btn"
              >
                {t.oneTime}
              </button>
              <button
                onClick={() => setRecurring(true)}
                className={`flex-1 py-3 rounded-full text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  recurring ? 'bg-warm-700 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
                role="radio"
                aria-checked={recurring}
                data-testid="donate-monthly-btn"
              >
                <RefreshCw className="w-4 h-4" />
                {t.monthly}
              </button>
            </div>

            {/* Preset Amounts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {presetAmounts.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => { setSelectedPreset(preset.id); setCustomAmount(""); }}
                  className={`py-4 rounded-xl border-2 font-semibold text-lg transition-all ${
                    selectedPreset === preset.id
                      ? 'border-warm-700 bg-warm-50 text-warm-700'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`donate-preset-${preset.id}`}
                  aria-label={`$${preset.amount}`}
                >
                  ${preset.amount}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <Label htmlFor="custom-amount" className="text-sm font-medium text-gray-700 mb-2 block">
                {t.customAmount}
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="custom-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  data-testid="donate-custom-amount"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setSelectedPreset(null); }}
                  className="pl-10 rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700"
                  placeholder={t.enterAmount}
                />
              </div>
            </div>

            {/* Optional Donor Info */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label htmlFor="donor-name" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.name}
                </Label>
                <Input
                  id="donor-name"
                  data-testid="donate-name"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  className="rounded-xl border-gray-200"
                  placeholder={t.namePlaceholder}
                />
              </div>
              <div>
                <Label htmlFor="donor-email" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.email}
                </Label>
                <Input
                  id="donor-email"
                  type="email"
                  data-testid="donate-email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="rounded-xl border-gray-200"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Emergency Survivor Fund toggle */}
            <label
              htmlFor="emergency-fund-toggle"
              className="flex items-start gap-3 bg-warm-50 border border-warm-100 rounded-xl p-4 mb-6 cursor-pointer"
            >
              <input
                id="emergency-fund-toggle"
                type="checkbox"
                checked={emergencyFund}
                onChange={(e) => setEmergencyFund(e.target.checked)}
                className="mt-1 accent-warm-700"
                data-testid="donate-emergency-fund"
              />
              <div className="text-sm">
                <p className="font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{t.emergencyFundLabel}</p>
                <p className="text-xs text-gray-600 mt-0.5">{t.emergencyFundNote}</p>
              </div>
            </label>

            <Button
              onClick={handleDonate}
              disabled={isProcessing || (!selectedPreset && (!customAmount || parseFloat(customAmount) < 1))}
              className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full py-6 text-lg"
              data-testid="donate-submit-btn"
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.redirecting}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  {recurring ? t.donateMonthly : t.donateNow}
                  {(selectedPreset || customAmount) && (
                    <span>— ${selectedPreset ? presetAmounts.find(p => p.id === selectedPreset)?.amount : customAmount}</span>
                  )}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center mt-4">
              {t.secureNote}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-16" role="region" aria-label={t.waysToGiveTitle}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            {t.waysToGiveTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.waysToGive?.map((w) => (
              <div key={w.title} className="bg-white border border-gray-100 rounded-xl p-5" data-testid={`ways-to-give-${w.title.replace(/\s+/g,'-').toLowerCase()}`}>
                <p className="font-semibold text-gray-900 mb-1 text-sm" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{w.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-sm text-gray-600">
            To discuss corporate sponsorships, workforce cohort funding, or in-kind donations, contact&nbsp;
            <a href="mailto:director@thepillarprojectbaltimore.org" className="text-warm-700 underline">director@thepillarprojectbaltimore.org</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
